import {useEffect, useState} from "react";
import {Controller, useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {useNavigate} from "react-router-dom";
import {useAuth} from "@/context/use-auth";
import {Heading, Subheading} from "@/components/tailwind/heading";
import {Input} from "@/components/tailwind/input";
import {Text} from "@/components/tailwind/text";
import {Textarea} from "@/components/tailwind/textarea";
import {Button} from "@/components/tailwind/button";
import ValidationAlert from "@/components/validation-alert";
import {Divider} from "@/components/tailwind/divider";
import {CreateOrderFormData, createOrderSchema, OrderFormData} from "@/models/order";
import {MemberListItem} from "@/models/member";
import {ServiceGroup, ServiceWithGroupId} from "@/models/service";
import {generateCompleteMemberName, getMemberData, getMembers, getMembersWithService} from "@/services/member-services";
import {createOrder} from "@/services/order-services";
import {getListOfServiceGroups, getListOfServices} from "@/services/service-services";
import {Loader2, UserPlus, WrenchIcon} from "lucide-react";
import MemberInfoCard from "@/components/member-info-card";
import {SubmissionChannel} from "@/models/submission-channel";
import {fetchAllSubmissionChannel} from "@/services/submission-channel-services";
import {useOrdersTranslation} from "@/i18n/hooks";
import {Select} from "@/components/tailwind/select";
import MemberSelectDialog from "@/components/member-select-dialog";
import SingleServiceSelector from "@/components/single-service-selector.tsx";
import {ProblemDetail} from "@/types/problem-details.ts";
import {isDefined} from "@/helpers/sanitizer.ts";
import {getCurrentMileageRate} from "@/services/mileage-rate-services.ts";
import {MileageRate} from "@/models/mileage-rate.ts";
import log from '@/lib/logger'

export default function CreateOrderPage() {
    const {t} = useOrdersTranslation()
    const {token} = useAuth();
    const navigate = useNavigate();

    const [isSubmitting, setIsSubmitting] = useState(false);
    // The order date is intentionally left empty so the office must choose it
    // deliberately at billing time (see issue #712).
    const [formData, setFormData] = useState<OrderFormData>({});

    const [clientDialogOpen, setClientDialogOpen] = useState(false);
    const [helperDialogOpen, setHelperDialogOpen] = useState(false);

    const [members, setMembers] = useState<MemberListItem[]>([]);
    const [availableHelpers, setAvailableHelpers] = useState<MemberListItem[]>([]);
    const [services, setServices] = useState<ServiceWithGroupId[]>([]);
    const [serviceGroups, setServiceGroups] = useState<ServiceGroup[]>([]);
    const [submissionChannels, setSubmissionChannels] = useState<SubmissionChannel[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isAllDataAvailable, setIsAllDataAvailable] = useState(false)

    const {
        register,
        handleSubmit,
        control,
        setValue,
        watch,
        setError,
        clearErrors,
        formState: {errors}
    } = useForm<CreateOrderFormData>({
        resolver: zodResolver(createOrderSchema),
        defaultValues: {
            orderDate: "",
        }
    });

    const selectedClientId = watch("clientId");
    const selectedServiceId = watch("serviceId");
    const selectedHelperId = watch("helperId");

    useEffect(() => {
        async function loadData() {
            setIsLoading(true);
            try {
                // Do parallel requests
                const [membersList, servicesList, serviceGroupsList, channels, mileageRate] = await Promise.all([
                    getMembers(token),
                    getListOfServices(token),
                    getListOfServiceGroups(token),
                    fetchAllSubmissionChannel(token),
                    getCurrentMileageRate(token),
                ]);

                setMembers(membersList || []);
                setAvailableHelpers(membersList || []);
                setServices(servicesList as ServiceWithGroupId[]);
                setServiceGroups(serviceGroupsList || []);
                setSubmissionChannels(channels || []);

                const hasMembers = isDefined<MemberListItem[]>(membersList) &&  membersList.length > 0
                const hasServices = isDefined<ServiceWithGroupId[]>(servicesList) && servicesList.length > 0
                const hasSubmissionChannels = isDefined<SubmissionChannel[]>(channels) && channels.length > 0
                const hasMileageRate = mileageRate !== null && isDefined<MileageRate>(mileageRate)
                setIsAllDataAvailable(hasMembers && hasServices && hasSubmissionChannels && hasMileageRate)
            } catch (error) {
                log.error("Error loading information for order creation dialog", error);
                setIsAllDataAvailable(false)
            } finally {
                setIsLoading(false);
            }
        }

        loadData();
    }, [token]);

    useEffect(() => {
        async function updateClient() {
            if (selectedClientId) {
                const client = await getMemberData(token, selectedClientId);
                if (client)
                    setFormData(prev => ({...prev, client}));
            } else {
                setFormData(prev => ({...prev, client: undefined}));
            }
        }

        updateClient();
    }, [selectedClientId, token]);

    useEffect(() => {
        async function updateServiceSelection() {
            if (selectedServiceId) {
                const service = services.find(s => s.id === selectedServiceId);
                setFormData(prev => ({...prev, service}));

                setValue("helperId", "");
                setFormData(prev => ({...prev, helper: undefined}));

                try {
                    const helpers = await getMembersWithService(token, selectedServiceId);

                    const activeHelpers = (helpers || []).filter(m => m.status === "ACTIVE_MEMBER");
                    
                    // Filter out the client from available helpers
                    const filteredHelpers = selectedClientId 
                        ? activeHelpers.filter(h => h.id !== selectedClientId)
                        : activeHelpers;
                    
                    setAvailableHelpers(filteredHelpers);

                    if (filteredHelpers.length === 0) {
                        setError("helperId", {
                            type: "manual",
                            message: "orderForm.create.errors.no-helpers-available"
                        });
                    } else {
                        clearErrors("helperId");
                    }
                } catch (error) {
                    log.error("Error loading available helpers:", error);

                    const activeMembers = members.filter(m => m.status === "ACTIVE_MEMBER");
                    
                    // Filter out the client from available helpers
                    const filteredMembers = selectedClientId 
                        ? activeMembers.filter(h => h.id !== selectedClientId)
                        : activeMembers;
                    
                    setAvailableHelpers(filteredMembers);

                    if (filteredMembers.length === 0) {
                        setError("helperId", {
                            type: "manual",
                            message: "orderForm.create.errors.no-active-members"
                        });
                    } else {
                        setError("helperId", {
                            type: "manual",
                            message: "orderForm.create.errors.fallback-all-active-member"
                        });
                    }
                }
            } else {
                setFormData(prev => ({...prev, service: undefined}));

                const activeMembers = members.filter(m => m.status === "Aktiv");
                
                // Filter out the client from available helpers
                const filteredMembers = selectedClientId 
                    ? activeMembers.filter(h => h.id !== selectedClientId)
                    : activeMembers;
                
                setAvailableHelpers(filteredMembers);
                
                if (filteredMembers.length === 0) {
                    setError("helperId", {
                        type: "manual",
                        message: "orderForm.create.errors.no-active-members"
                    });
                } else {
                    clearErrors("helperId");
                }
            }
        }

        updateServiceSelection();
    }, [selectedServiceId, selectedClientId, services, setValue, token, members, clearErrors, setError]);

    useEffect(() => {
        async function updateHelper() {
            if (selectedHelperId) {
                const helper = await getMemberData(token, selectedHelperId);
                if (helper)
                    setFormData(prev => ({...prev, helper}));

                if (selectedClientId === selectedHelperId) {
                    setError("helperId", {
                        type: "manual",
                        message: "orderForm.create.errors.client-cannot-be-helper"
                    });
                } else {
                    clearErrors("helperId");
                }
            } else {
                setFormData(prev => ({...prev, helper: undefined}));
            }
        }

        updateHelper();
    }, [selectedHelperId, selectedClientId, token, setError, clearErrors]);

    const handleClientSelect = (client: MemberListItem) => {
        setValue("clientId", client.id);
        setClientDialogOpen(false);

        if (client.id === selectedHelperId) {
            setValue("helperId", "");
            setFormData(prev => ({...prev, helper: undefined}));
        }
    };

    const handleHelperSelect = (helper: MemberListItem) => {
        setValue("helperId", helper.id);
        setHelperDialogOpen(false);

        // Wenn der ausgewählte Helfer auch als Client ausgewählt ist, Fehler setzen
        if (helper.id === selectedClientId) {
            setError("helperId", {
                type: "manual",
                message: "orderForm.create.errors.client-cannot-be-helper"
            });
        } else {
            clearErrors("helperId");
        }
    };

    const onSubmit = async (data: CreateOrderFormData) => {
        if (data.clientId === data.helperId) {
            setError("helperId", {
                type: "manual",
                message: "orderForm.create.errors.client-cannot-be-helper"
            });
            return;
        }

        setIsSubmitting(true);

        try {
            const orderId = await createOrder(token, data);

            if (orderId) {
                navigate(`/orders/${orderId}`);
            } else {
                navigate("/orders");
            }
        } catch (error) {
            log.error("Error while creating the order:", error);
            
            if ((error as ProblemDetail).validationIssues) {
                (error as ProblemDetail).validationIssues?.map(issue => {
                    setError(
                        `root.${issue.field}`, {
                            message: issue.message
                        }
                    )
                })
            } else {
                // Nur generische Fehlermeldung wenn keine spezifischen Validierungsfehler vorhanden sind
                setError("root", {
                    type: "serverError",
                    message: "orderForm.create.errors.server-issue"
                });
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-primary"/>
                <span className="ml-2">{t("orderForm.create.loadingData")}</span>
            </div>
        );
    }

    return (
        <>
            <form onSubmit={handleSubmit(onSubmit)} className="mx-auto max-w-4xl">
                <Heading>{t("orderForm.create.heading")}</Heading>

                <Divider className="my-10 mt-6"/>

                {(!isAllDataAvailable || errors.root) && (
                    <section aria-label={t("orderForm.create.errors.ariaLabel.error-section")}>
                        {!isAllDataAvailable && (
                            <ValidationAlert message={t("orderForm.create.errors.settings-missing")} />
                        )}

                        {errors.root?.message && (
                            <ValidationAlert message={t(errors.root.message)}/>
                        )}

                        {errors.root?.mileageRate && (
                            <ValidationAlert
                                message={t(errors.root.mileageRate.message || "orderForm.create.errors.mileage-rate-issue")}/>
                        )}
                        
                        {errors.root?.orderDate && (
                            <ValidationAlert
                                message={t(errors.root.orderDate.message || "orderForm.create.errors.order-date-issue")}/>
                        )}
                        
                        {errors.root?.pricingScheme && (
                            <ValidationAlert
                                message={t(errors.root.pricingScheme.message || "orderForm.create.errors.pricing-scheme-issue")}/>
                        )}
                    </section>
                )}

                <section className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
                    <div className="space-y-1">
                        <Subheading>{t("orderForm.create.order-date.heading")}</Subheading>
                        <Text>{t("orderForm.create.order-date.description")}</Text>
                    </div>
                    <div>
                        <Input
                            id="orderDate"
                            type="date"
                            data-testid="order-date"
                            {...register("orderDate")}
                            invalid={!!errors.orderDate}
                        />
                        {errors.orderDate && (
                            <ValidationAlert
                                message={t(errors.orderDate.message || "orderForm.create.order-date-issue")}/>
                        )}
                    </div>
                </section>

                <Divider className="my-10" soft/>

                <section className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
                    <div className="space-y-1">
                        <Subheading id="clientIdHeadline">{t("orderForm.create.client.heading")}</Subheading>
                        <Text id="clientIdDescription">{t("orderForm.create.client.description")}</Text>
                    </div>
                    <div>
                        <div className="flex gap-2 mb-2">
                            <input
                                hidden
                                {...register("clientId")}

                            />
                            <Button
                                type="button"
                                onClick={() => setClientDialogOpen(true)}
                                className="flex gap-2 items-center w-full justify-between"
                                data-testid="order-client"
                            >
                                <span>
                                    {formData.client
                                        ? generateCompleteMemberName(formData.client as any)
                                        : t("orderForm.create.client.button")}
                                </span>
                                <UserPlus className="h-5 w-5"/>
                            </Button>
                        </div>

                        {errors.clientId && (
                            <ValidationAlert
                                message={t(errors.clientId.message || "orderForm.create.errors.client-issue")}/>
                        )}

                        {formData.client && (
                            <div className="mt-4">
                                <MemberInfoCard member={formData.client} showPoints/>
                            </div>
                        )}
                    </div>
                </section>

                <Divider className="my-10" soft/>

                <section className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
                    <div className="space-y-1">
                        <Subheading id="serviceIdHeadline">{t("orderForm.create.service.heading")}</Subheading>
                        <Text id="serviceIdDescription">{t("orderForm.create.service.description")}</Text>
                    </div>
                    <div>
                        <Controller
                            name="serviceId"
                            control={control}
                            render={({field}) => (
                                <SingleServiceSelector
                                    services={services}
                                    groups={serviceGroups}
                                    selectedServiceId={field.value}
                                    onChange={(selected) => {
                                        field.onChange(selected);
                                    }}
                                    className="w-full"
                                    placeholder={t("orderForm.create.service.placeholder")}
                                    data-testid="order-service"
                                />
                            )}
                        />
                        {errors.serviceId && (
                            <ValidationAlert
                                message={t(errors.serviceId.message || "orderForm.create.errors.invalid-service-uuid")}/>
                        )}
                    </div>
                </section>

                <Divider className="my-10" soft/>

                {selectedServiceId && (
                    <>
                        <section className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
                            <div className="space-y-1">
                                <Subheading id="helperIdHeadline">{t("orderForm.create.helper.heading")}</Subheading>
                                <Text id="helperIdDescription">{t("orderForm.create.helper.description")}</Text>
                            </div>
                            <div>
                                <div className="flex gap-2 mb-2">
                                    <input
                                        hidden
                                        {...register("helperId")}
                                    />

                                    <Button
                                        type="button"
                                        onClick={() => setHelperDialogOpen(true)}
                                        className="flex gap-2 items-center w-full justify-between"
                                        disabled={availableHelpers.length === 0}
                                        data-testid="order-helper"
                                    >
                                        <span>
                                            {formData.helper
                                                ? generateCompleteMemberName(formData.helper as any)
                                                : t("orderForm.create.helper.button")}
                                        </span>
                                        <WrenchIcon className="h-5 w-5"/>
                                    </Button>
                                </div>

                                {errors.helperId && (
                                    <ValidationAlert
                                        message={t(errors.helperId.message || "orderForm.create.errors.helper-issue")}/>
                                )}

                                {formData.helper && (
                                    <div className="mt-4">
                                        <MemberInfoCard member={formData.helper}/>
                                    </div>
                                )}
                            </div>
                        </section>

                        <Divider className="my-10" soft/>
                    </>
                )}

                <section className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
                    <div className="space-y-1">
                        <Subheading id="submissionChannelIdHeadline">{t("orderForm.create.submissionChannel.heading")}</Subheading>
                        <Text id="submissionChannelIdDescription">{t("orderForm.create.submissionChannel.description")}</Text>
                    </div>
                    <div>
                        <Select
                            aria-labelledby="submissionChannelIdHeadline submissionChannelIdDescription"
                            data-testid="order-submission-channel"
                            {...register("submissionChannelId")}
                            invalid={!!errors.submissionChannelId}
                        >
                            {submissionChannels.map((channel) => (
                                <option key={channel.id} value={channel.id} selected={channel.isDefault} data-testid="submission-channel-option">
                                    {channel.name}
                                </option>
                            ))}
                        </Select>
                        {errors.submissionChannelId && (
                            <ValidationAlert
                                message={t(errors.submissionChannelId.message || "orderForm.create.errors.submission-channel-issue")}/>
                        )}
                    </div>
                </section>

                <Divider className="my-10" soft/>

                <section className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
                    <div className="space-y-1">
                        <Subheading id="commentsHeadline">{t("orderForm.create.comments.heading")}</Subheading>
                        <Text id="commentsDescription">{t("orderForm.create.comments.description")}</Text>
                    </div>
                    <div>
                        <Textarea
                            id="comments"
                            aria-labelledby="commentsHeadline commentsDescription"
                            data-testid="order-comments"
                            {...register("comments")}
                            rows={4}
                            placeholder={t("orderForm.create.comments.placeholder")}
                            invalid={!!errors.comments}
                        />
                        {errors.comments && (
                            <ValidationAlert
                                message={t(errors.comments.message || "orderForm.create.errors.comments-issue")}/>
                        )}
                    </div>
                </section>

                <Divider className="my-10" soft/>

                <section className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
                    <div>
                    </div>
                    <div className="flex justify-end gap-4">
                        <Button
                            aria-label={t("orderForm.create.buttons.cancel.ariaLabel")}
                            plain
                            type="button"
                            onClick={() => navigate("/orders")}
                            data-testid="cancel-order-button"
                        >
                            {t("orderForm.create.buttons.cancel.text", "Abbrechen")}
                        </Button>

                        <Button
                            aria-label={t("orderForm.create.buttons.submit.ariaLabel")}
                            type="submit"
                            disabled={!isAllDataAvailable || isSubmitting}
                            data-testid="save-order-button"
                        >
                            {isSubmitting ? (
                                <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin"/>
                                    {t("orderForm.create.buttons.submit.active")}
                                </>
                            ) : (
                                t("orderForm.create.buttons.submit.text")
                            )}
                        </Button>
                    </div>
                </section>
            </form>

            <MemberSelectDialog
                isOpen={clientDialogOpen}
                onClose={() => setClientDialogOpen(false)}
                onSelect={handleClientSelect}
                members={members}
                title={t("orderForm.create.client.dialog-title")}
                excludeMemberId={selectedHelperId}
            />

            <MemberSelectDialog
                isOpen={helperDialogOpen}
                onClose={() => setHelperDialogOpen(false)}
                onSelect={handleHelperSelect}
                members={availableHelpers}
                title={t("orderForm.create.helper.dialog-title")}
                excludeMemberId={selectedClientId}
            />
        </>
    );
}
